<template>
  <FairSurveyDialog
    v-model:open="visible"
    :title="configTitle"
    :show-title="isWelcomeStep"
    :exit-label="isWelcomeStep ? 'Назад' : 'Сохранить и выйти'"
    :show-progress="showProgress"
    :progress-percent="progressPercentage"
    :progress-label="progressLabel"
    :loading="lookModal.isLoadingConfig"
    loading-label="Загрузка опроса…"
    :show-footer="showFooter"
    :show-saved="isTinderLikeStep"
    saved-label="Данные сохранены"
    :compact="!isDesktop"
    @close="handleHideLookModal"
  >
    <DynamicStepRenderer />

    <template #actions>
      <!-- The swipe deck advances itself; the footer only reports the save. -->
      <template v-if="!isTinderLikeStep">
        <FairButton
          v-if="showBackButton"
          label="Назад"
          variant="tonal"
          size="m"
          :rounded="false"
          @click="prevStep"
        />
        <FairButton
          v-if="showNextButton"
          :label="nextButtonLabel"
          variant="primary"
          size="m"
          :rounded="false"
          :disabled="lookModal.isLoading"
          @click="handleNext"
        />
      </template>
    </template>
  </FairSurveyDialog>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { FairSurveyDialog } from "@/ui/patterns/FairSurveyDialog";
import { FairButton } from "@/ui/primitives/FairButton";

import { useLookModal } from "@/features/look/store";
import DynamicStepRenderer from "./LookSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const lookStore = useLookModal();
const lookModal = computed(() => lookStore.lookModal);
const currentStep = computed(() => lookStore.currentStep);
const currentStepIndex = computed(() => lookStore.lookModal.currentStepIndex);
const totalSteps = computed(() => lookStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);

const configTitle = computed(() => {
  return lookStore.lookModal.config?.title || "Подбор образа";
});

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});

const isTinderLikeStep = computed(
  () => currentStep.value?.type === "tinder_like"
);

/**
 * Welcome has nothing to measure yet, and the final screen is past the last
 * step — the config marks it `completion-1`.
 */
const showProgress = computed(() => {
  if (!currentStep.value || isWelcomeStep.value) return false;
  return currentStep.value.id !== "completion-1";
});

const progressLabel = computed(
  () => `${currentStepIndex.value + 1} из ${totalSteps.value}`
);

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((currentStepIndex.value + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide back button on welcome step
  if (stepType === "welcome") return false;
  
  // Hide back button on completion step
  if (stepType === "completion") return false;
  
  // Hide back button on steps that handle navigation themselves
  if (stepType === "tinder_like") return false;
  
  return currentStepIndex.value > 0;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide next button on completion step
  if (stepType === "completion") {
    return false;
  }
  
  // Hide next button on steps that handle navigation themselves
  if (stepType === "tinder_like") {
    return false;
  }
  
  return currentStepIndex.value < totalSteps.value - 1;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === "welcome") return "Начать тест";
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  if (stepType === "completion") {
    return false;
  }
  
  return true;
});

function handleHideLookModal() {
  lookStore.hideLookModal();
}

function prevStep() {
  lookStore.prevStep();
}

function handleNext() {
  if (!lookStore.validateCurrentStep()) {
    return;
  }
  
  lookStore.nextStep();
}

watch(
  () => lookModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
  }
);
</script>
